import { SvgIconProps } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Action } from 'kbar';
import React, { useContext } from 'react';

import { MainSettingsIcon } from 'src/components/icons';
import { NavItemList } from 'src/components/sidebar/sidebar-items/NavItemList';
import { SidebarFolderItem } from 'src/components/sidebar/sidebar-items/SidebarFolderItem';
import { SidebarListItem } from 'src/components/sidebar/sidebar-items/SidebarListItem';
import { SidebarSectionTitle } from 'src/components/sidebar/sidebar-items/SidebarSectionTitle';
import * as Constants from 'src/constants';
import {
  GENERAL_SETTINGS_PAGE,
  HELP_CENTER_PAGE,
  PAYMENTS_INVOICES_PAGE,
  PAYMENTS_PAYMENT_LINKS_PAGE,
  PAYMENTS_SERVICES_PAGE,
  PAYMENTS_SUBSCRIPTIONS_PAGE,
  PERM_MOD_SETTINGS_MAIN_PG,
  PROFILE_PAGE,
} from 'src/constants';
import { FlagsContext, RouteContext } from 'src/context';
import history from 'src/history';
import { useKeyboardShortcuts } from 'src/hooks/useKeyboardShortcuts';
import useNavigate from 'src/hooks/useNavigate';
import { usePlanStatus } from 'src/hooks/usePlanStatusHook';
import { useSidebarNavigationShortcut } from 'src/hooks/useSidebarNavigationShortcut';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { useUserNotification } from 'src/hooks/useUserNotification';
import RowDivider from 'src/legacy/components/RowDivider';
import { useSettingsPageRedirection } from 'src/legacy/components/Settings/useSettingsPageRedirection';
import { useRouteAccess } from 'src/routes/routeAccess';
import { useCustomFields } from 'src/services/api/customFieldsApi';
import {
  CustomFieldEntityType,
  selectActiveCompaniesMap,
} from 'src/store/clients/types';
import { GetCurrentUserFromState } from 'src/store/storeUtils';
import {
  toggleHelpCenterModalAction,
  togglePrimarySidebarMobile,
} from 'src/store/ui/actions';
import { BlackHeadings, NonHoverBorder } from 'src/theme/colors';
import { UrlUtils } from 'src/utils';
import { applyQueryParams } from 'src/utils/ExtensionUtils';
import { hasPermission } from 'src/utils/UserUtils';

import { ClientExperienceNavItem } from './ClientExperienceNavItem';
import { CollapseItem } from './CollapseItem';
import { appNavItemIdPrefix, useSidebarItems } from './useSidebarItems';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      height: '100%',
    },
    nested: {
      padding: theme.spacing(0.5, 0, 0.5, 3),
    },
    sidebarItemDivider: {
      padding: theme.spacing(0, 1),
      '& hr': {
        backgroundColor: NonHoverBorder,
      },
    },
    menuList: {
      padding: theme.spacing(1),
      paddingBottom: 0,
      minWidth: 170,
      outline: 'none',
    },
    copyText: {
      wordBreak: 'break-all',
      color: BlackHeadings,
    },
  }),
);
export interface NavSection {
  htmlId: string;
  title: string;
  navItems: Array<NavItem>;
  canAccess: boolean;
  icon?: (props: SvgIconProps) => JSX.Element;
  navItemsWithFolders?: Array<NavItem>;
}

/**
 * Checks if the item is a (non-folder) NavItem
 */
export function isNavItem(item: any): item is NavItem {
  return (
    Boolean(item) &&
    'htmlId' in item &&
    'label' in item &&
    'path' in item &&
    !item.isFolder
  );
}

export interface NavItem {
  htmlId: string;
  label: string;
  path: string;
  isExternalLink?: boolean;
  icon?: (props: SvgIconProps) => JSX.Element;
  canAccess?: boolean;
  subItems?: Array<NavItem>;
  shouldCollapse?: boolean;
  selected?: boolean;
  children?: React.ReactNode;
  shortcut?: string;
  tooltipTitle?: React.ReactNode;
  disabled?: boolean;
  priority?: number;
  isNonCoreApp?: boolean;
  queryParams?: Record<string, string>[];
  id?: string;
  isFolder?: boolean;
  folderPath?: string;
  isHidden?: boolean;
}

type NavOptions = {
  path: string;
  isExternalLink?: boolean;
  isCollapseItem?: boolean;
  isNonCoreApp?: boolean;
};
const SidebarNav: React.FC = () => {
  const classes = useStyles();
  const { pathname } = useContext(RouteContext);
  const { navigate } = useNavigate();
  const isClient = useAppSelector((state) => state.user.isClient);
  const loggedInUser = useAppSelector((state) =>
    isClient ? GetCurrentUserFromState(state) : undefined,
  );
  const { EnableManyCompanies, EnableAppSetup, EnableBillingConsolidation } =
    useContext(FlagsContext);
  const { routeAccess } = useRouteAccess();
  const clientCustomFieldsMap = useCustomFields(CustomFieldEntityType.CLIENT);
  const companyCustomFieldsMap = useCustomFields(CustomFieldEntityType.COMPANY);

  useUserNotification(isClient);

  const loggedInUserCompany = useAppSelector((state) => {
    if (!isClient || !loggedInUser) return undefined;
    const activeCompaniesMap = selectActiveCompaniesMap(state);
    if (EnableManyCompanies) {
      const { activeCompanyId } = state.activeCompany;
      return activeCompanyId
        ? activeCompaniesMap.get(activeCompanyId)
        : undefined;
    }
    const companyId = loggedInUser.fields.companyId ?? '';
    return companyId
      ? state.clients.activeCompanies.find(({ id }) => id === companyId)
      : undefined;
  });
  const dispatch = useAppDispatch();
  const { isTrialing } = usePlanStatus();
  const permissions = useAppSelector((state) => state.user.data?.permissions);
  const hasMainSettingsPermission = hasPermission(
    PERM_MOD_SETTINGS_MAIN_PG,
    permissions,
  );
  const settingsLandingPage =
    !isClient && hasMainSettingsPermission
      ? GENERAL_SETTINGS_PAGE
      : PROFILE_PAGE;
  const { goToSettings } = useSettingsPageRedirection(
    true,
    settingsLandingPage.path,
  );
  const isPrimarySidebarOpenOnMobile = useAppSelector(
    (state) => state.ui.primarySideBarMobile.isOpen,
  );

  const getNavHandler =
    (navOptions: NavOptions & Pick<NavItem, 'queryParams'>): (() => void) =>
    () => {
      const {
        path,
        isExternalLink,
        isCollapseItem,
        isNonCoreApp,
        queryParams,
      } = navOptions;

      // when sidebar item has external link, open it in new tab
      let fullPath = UrlUtils.GetFullUrl(path);

      // Apply query parameters if they exist and we have client/company context
      if (isExternalLink && queryParams?.length) {
        fullPath = applyQueryParams(
          fullPath,
          queryParams,
          loggedInUser,
          loggedInUserCompany,
          clientCustomFieldsMap,
          companyCustomFieldsMap,
        );
      }

      if (isExternalLink) {
        window.open(fullPath, '_blank');
        return;
      }

      // otherwise, navigate to path
      navigate(path, { forceReload: isNonCoreApp || false });

      // after navigating to path, close primary sidebar on mobile
      if (isPrimarySidebarOpenOnMobile) {
        // if it's a collapse item, do not close primary sidebar
        if (isCollapseItem) return;
        dispatch(togglePrimarySidebarMobile({ isOpen: false }));
      }
    };

  const { sections: mainSidebarSections, isLoadingInstalls } =
    useSidebarItems('main');
  const { sections: settingsSidebarSections } = useSidebarItems('settings');

  const isSettingsPage = pathname.includes('settings');

  const sidebarSections = React.useMemo(
    () => (isSettingsPage ? settingsSidebarSections : mainSidebarSections),
    [pathname, settingsSidebarSections, mainSidebarSections],
  );

  // Determine the first accessible nav item for redirection
  // Our sidebar is divied into three sections Home, Apps and Preferences
  // Home is not accessible for client, so we're skipping it
  const firstAccessibleNavItem = React.useMemo(
    () =>
      (isClient
        ? mainSidebarSections.at(1)
        : mainSidebarSections?.at(0)
      )?.navItems.find((item) => item.canAccess && !item.isHidden),
    [mainSidebarSections, isClient],
  );

  /**
   * This holds the main sidebar command bar actions (shortcuts)
   */
  const mainSidebarCommandBarActions: Action[] = React.useMemo(() => {
    // For Sidebar 2.0, we're adding customization, module mangement and settings to commandbar.
    // For this reason, we're looping over all items in main sidebar.
    const navItems = mainSidebarSections
      .map((x) => {
        const items = x.navItems;
        if (items.some((i) => i.subItems)) {
          if (EnableAppSetup) {
            // flatten out billing modules
            return items.flatMap((item) =>
              item.subItems ? [item, ...item.subItems] : item,
            );
          } else {
            return [...items, ...items.flatMap((y) => y.subItems)]; // Invoices, Subscription, Customization and Module Management are sub items
          }
        }
        return items;
      })
      .flat(Infinity) // Flattening the array so all items are rooted to base level
      .filter(
        (x) =>
          isNavItem(x) &&
          Boolean(x.canAccess) &&
          x.htmlId.includes('sidebar') &&
          !x.isHidden,
      );

    const actions: Action[] = [
      ...(navItems as NavItem[]).map(({ htmlId, label, path, disabled }) => ({
        section: 'Navigation',
        id: htmlId,
        name: `Go to ${label}`,
        disabled,
        perform: () => (!disabled ? navigate(path) : null), // Disabled items can not be navigated to.
      })),
      // Adding sidebar section manually because it's not part of main sidebar nav items
      {
        section: 'Navigation',
        id: 'internal-settings',
        name: 'Go to Settings',
        perform: () => goToSettings(),
      },
    ];

    const isBillingModuleEnabled = mainSidebarSections
      .flatMap((section) => section.navItems)
      .some((item) => item.htmlId === 'sidebar-payments' && item.canAccess);

    // When EnableBillingConsolidation is enabled, add payment-related items to command bar
    // even though they're not in the sidebar (they're consolidated under "Payments")
    if (EnableBillingConsolidation && !isClient && isBillingModuleEnabled) {
      const paymentItems: Action[] = [];

      // Add Invoices
      if (routeAccess.allowInvoices && routeAccess.allowSubscriptionPage) {
        paymentItems.push({
          section: 'Navigation',
          id: 'sidebar-invoices',
          name: `Go to ${Constants.INVOICES_PAGE.label}`,
          perform: () => navigate(PAYMENTS_INVOICES_PAGE.path),
        });
      }

      // Add Subscriptions
      if (routeAccess.allowPaymentsPage && routeAccess.allowSubscriptionPage) {
        paymentItems.push({
          section: 'Navigation',
          id: 'sidebar-subscriptions',
          name: `Go to ${Constants.SUBSCRIPTION_PAGE.label}`,
          perform: () => navigate(PAYMENTS_SUBSCRIPTIONS_PAGE.path),
        });
      }

      // Add Payment Links
      if (routeAccess.allowPaymentsPage && routeAccess.allowSubscriptionPage) {
        paymentItems.push({
          section: 'Navigation',
          id: 'sidebar-payment-links',
          name: `Go to ${Constants.PAYMENT_LINKS_PAGE.label}`,
          perform: () => navigate(PAYMENTS_PAYMENT_LINKS_PAGE.path),
        });
      }

      // Add Products/Services
      if (routeAccess.allowPaymentsPage && routeAccess.allowSubscriptionPage) {
        paymentItems.push({
          section: 'Navigation',
          id: 'sidebar-products',
          name: `Go to ${Constants.PRODUCTS_PAGE.label}`,
          perform: () => navigate(PAYMENTS_SERVICES_PAGE.path),
        });
      }

      actions.push(...paymentItems);
    }

    return actions;
  }, [
    mainSidebarSections,
    hasMainSettingsPermission,
    EnableBillingConsolidation,
    isClient,
    routeAccess,
    navigate,
  ]);

  // register shortcuts actions for main sidebar.
  useKeyboardShortcuts(mainSidebarCommandBarActions);

  // Enable Option+Up/Down keyboard navigation for sidebar (internal users only)
  useSidebarNavigationShortcut();

  /**
   * this use effect will redirect the to the first available sidebar section
   * for the user. Internal user will go to get started if available
   * otherwise user is redirect to first sidebar nav item
   */
  React.useEffect(() => {
    if (pathname === '/' || pathname === '/no-apps') {
      // Client doesn't have access to CRM and Home (for now).
      // First accessible section for client would have to come from sidebarSections[1]
      // Or else redirection to first nav item won't be possible

      if (firstAccessibleNavItem) {
        // Determine the appropriate redirect path based on the first accessible navigation item.
        // If the first accessible item is an external link, instead of redirecting directly to the external link,
        // we redirect to the apps page using the app ID. This page provides information about the app,
        // and the user can then choose to visit the external link from there.
        const redirectPath = firstAccessibleNavItem.isExternalLink
          ? `/apps?id=${firstAccessibleNavItem.htmlId.replace(
              appNavItemIdPrefix,
              '',
            )}`
          : firstAccessibleNavItem.path;

        // Avoid redirecting back to the current path
        if (redirectPath !== pathname) {
          history.push(redirectPath);
        }
      } else if (pathname !== '/no-apps' && isClient && !isLoadingInstalls) {
        // if the current user is a client and there are no accessible nav items
        // redirect to the no apps page if it is not the current page
        // only redirect after installs data has finished loading
        history.push('/no-apps');
      } else if (!isClient && !isLoadingInstalls) {
        console.error('No accessible sidebar section found for redirection');
      }
    }
  }, [firstAccessibleNavItem, pathname, isClient, isLoadingInstalls]);

  /**
   * This method allow to check if collapse item
   * should remain open or not. The logic is
   * to check if pathname root node is the
   * same as collapse childs path root node.
   * If so, the collapse should remains open,
   * otherwise it can collapse.
   * @param item
   */
  const getCollapseItemState = (item: NavItem) => {
    if (!item.subItems) return false;
    const pathRouteNode = pathname.split('/').at(1);

    return item.subItems.some(
      (subItem) => subItem.path.split('/')[1] === pathRouteNode,
    );
  };

  const accessibleSections = sidebarSections.filter((s) => s.canAccess);

  const isHelpCenterModalOpen = useAppSelector(
    (state) => state.ui.helpCenterModal.isOpen,
  );

  const openHelpCenterModal = () => {
    dispatch(toggleHelpCenterModalAction(true));
  };
  const showDivider = (index: number) =>
    index !== sidebarSections.length - 1 && // Divider not to be displayed for last section
    !isSettingsPage && // Settings page do not have dividers
    !isClient && // Client do not have multiple section, hence no divider
    sidebarSections[index + 1]?.canAccess; // There's next item, so we can display divider

  const getIsNavItemSelected = (navItem: NavItem) => {
    // navItem is explicitly selected
    if (navItem.selected) return true;
    // explicitly handle dashboard's path at the root
    if (navItem.path === '/') return pathname === '/';
    // navItem is selected if their path is the start of the current pathname
    return pathname.startsWith(navItem.path);
  };

  return (
    <div className={classes.root}>
      <div>
        {sidebarSections.map((section, index) => {
          let { navItems } = section;
          let selectedHiddenNavItem: NavItem | undefined;
          if (EnableAppSetup && section.navItemsWithFolders) {
            navItems = section.navItemsWithFolders;
            selectedHiddenNavItem = section.navItems.find(
              (item) => item.isHidden && getIsNavItemSelected(item),
            );
          }
          return (
            <React.Fragment key={section.htmlId}>
              {section.canAccess && (
                <>
                  {/* Displaying section title and action */}
                  {accessibleSections.length > 1 && section.title && (
                    <SidebarSectionTitle
                      htmlId={section.htmlId}
                      sectionTitle={section.title}
                    >
                      {section.icon}
                    </SidebarSectionTitle>
                  )}

                  {/* Displaying section nav items */}
                  {navItems.map(
                    (navItem) =>
                      navItem.canAccess &&
                      !navItem.isHidden &&
                      (navItem.subItems ? (
                        EnableAppSetup ? (
                          <SidebarFolderItem
                            key={navItem.htmlId}
                            htmlId={navItem.htmlId}
                            label={navItem.label}
                            isOpen={
                              navItem.shouldCollapse ||
                              getCollapseItemState(navItem)
                            }
                            IconElement={navItem.icon}
                          >
                            <div className={classes.nested}>
                              <NavItemList
                                items={navItem.subItems
                                  .filter(
                                    (sub) => sub.canAccess && !sub.isHidden,
                                  )
                                  .map((sub) => ({
                                    id: sub.htmlId,
                                    label: sub.label,
                                    selected: getIsNavItemSelected(sub),
                                    disabled: sub.disabled,
                                    tooltipTitle: sub.tooltipTitle,
                                    itemChildren: sub.children,
                                    onClick: getNavHandler({
                                      path: sub.path,
                                      isExternalLink: sub.isExternalLink,
                                      isNonCoreApp: sub.isNonCoreApp,
                                      queryParams: sub.queryParams,
                                    }),
                                  }))}
                              />
                            </div>
                          </SidebarFolderItem>
                        ) : (
                          <CollapseItem
                            key={navItem.htmlId}
                            htmlId={navItem.htmlId}
                            label={navItem.label}
                            show={
                              navItem.shouldCollapse ||
                              getCollapseItemState(navItem)
                            }
                            IconElement={navItem.icon}
                            handleClick={getNavHandler({
                              path: navItem.path,
                              isCollapseItem: true,
                            })}
                            disabled={navItem.disabled}
                            tooltipTitle={navItem.tooltipTitle}
                          >
                            <div className={classes.nested}>
                              {navItem.subItems.map((subNavItem) =>
                                subNavItem.canAccess ? (
                                  <SidebarListItem
                                    key={subNavItem.htmlId}
                                    htmlId={subNavItem.htmlId}
                                    label={subNavItem.label}
                                    selected={getIsNavItemSelected(subNavItem)}
                                    handleClick={getNavHandler({
                                      path: subNavItem.path,
                                      isNonCoreApp: subNavItem.isNonCoreApp,
                                    })}
                                  />
                                ) : null,
                              )}
                            </div>
                          </CollapseItem>
                        )
                      ) : (
                        <SidebarListItem
                          key={navItem.htmlId}
                          htmlId={navItem.htmlId}
                          label={navItem.label}
                          tooltipTitle={navItem.tooltipTitle}
                          disabled={navItem.disabled}
                          selected={getIsNavItemSelected(navItem)}
                          handleClick={getNavHandler({
                            path: navItem.path,
                            isExternalLink: navItem.isExternalLink,
                            isNonCoreApp: navItem.isNonCoreApp,
                            queryParams: navItem.queryParams,
                          })}
                          IconElement={navItem.icon}
                        >
                          {navItem.children}
                        </SidebarListItem>
                      )),
                  )}
                  {selectedHiddenNavItem && (
                    <SidebarListItem
                      htmlId={selectedHiddenNavItem.htmlId}
                      label={selectedHiddenNavItem.label}
                      tooltipTitle={selectedHiddenNavItem.tooltipTitle}
                      disabled={selectedHiddenNavItem.disabled}
                      selected
                      IconElement={selectedHiddenNavItem.icon}
                    >
                      {selectedHiddenNavItem.children}
                    </SidebarListItem>
                  )}
                </>
              )}
              {showDivider(index) && (
                <div className={classes.sidebarItemDivider}>
                  <RowDivider mt={1} mb={1} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {!isSettingsPage && (
        <div>
          {!isClient && !isTrialing && (
            <SidebarListItem
              htmlId="sidebar-referrals"
              label="Referrals"
              selected={pathname === Constants.REFERRAL_PAGE_V2.path}
              handleClick={getNavHandler({
                path: Constants.REFERRAL_PAGE_V2.path,
              })}
              IconElement={Constants.REFERRAL_PAGE_V2.icon}
            />
          )}
          {!isClient && (
            <SidebarListItem
              htmlId="sidebar-help-center"
              label={HELP_CENTER_PAGE.label}
              selected={isHelpCenterModalOpen}
              handleClick={openHelpCenterModal}
              IconElement={Constants.HELP_CENTER_PAGE.icon}
            />
          )}

          <SidebarListItem
            htmlId="sidebar-settings"
            label="Settings"
            selected={pathname === Constants.PROFILE_PAGE.path}
            handleClick={() => {
              goToSettings();
            }}
            IconElement={MainSettingsIcon}
          />
          {!isClient && <ClientExperienceNavItem />}
        </div>
      )}
    </div>
  );
};

export default SidebarNav;
